import React, { useState, useEffect } from "react";
import HeaderComponent from "@components/common/Header/Header";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import CustomTable from "@/components/common/table/CustomTable";
import EditIcon from "@styles/all-images/icons/edit.svg";
import DownloadIcon from "@styles/all-images/icons/download.svg";
import ListIcon from "@styles/all-images/icons/list-icon.svg";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import UnarchiveIcon from "@styles/all-images/icons/unarchived.svg";
import dateHandler from "@/composables/dateUtil";
interface UserType {
  id: string;
  user_type: string;
}

const CMSPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isArchiveView, setIsArchiveView] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [categories, setCategories] = useState<UserType[]>([]);
  const [selectedUserType, setSelectedUserType] = useState<string | null>(null);
  const { toStandardDisplayDate } = dateHandler();

  const fetchUserTypes = async () => {
    try {
      const { unAuthenticatedCall: getUserTypes } =
        actionsHandler("userTypeList");
      const response = await dispatch(
        getUserTypes({
          method: "get",
        }),
      );

      if (response.ok && response.data) {
        setCategories(
          response.data.map((item: any) => ({
            id: item.id,
            user_type: item.user_type,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  };

  useEffect(() => {
    fetchUserTypes();
  }, []);

  const fetchData = async (params: {
    page: number;
    limit: number;
    search?: string;
    sortColumn?: string;
    sortDirection?: "asc" | "desc";
  }) => {
    try {
      const { unAuthenticatedCall: getCMSContent } =
        actionsHandler("cmsContent");

      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        limit: params.limit.toString(),
        ...(params.search && { search: params.search }),
        ...(params.sortColumn && { sort_by: params.sortColumn }),
        ...(params.sortDirection && { sort_direction: params.sortDirection }),
        is_archive: isArchiveView ? "true" : "false",
        ...(selectedUserType && { user_type: selectedUserType }),
      }).toString();

      const response = await dispatch(
        getCMSContent({
          method: "get",
          customQuery: queryParams,
        }),
      );

      if (response.ok) {
        return {
          data: {
            data: response.data.data,
            meta: {
              count: response.data.total,
              page: params.page,
              limit: params.limit,
            },
          },
        };
      }

      return {
        data: {
          data: [],
          meta: {
            count: 0,
            page: 1,
            limit: 10,
          },
        },
      };
    } catch (error) {
      console.error("Error fetching CMS content:", error);
      return {
        data: {
          data: [],
          meta: {
            count: 0,
            page: 1,
            limit: 10,
          },
        },
      };
    }
  };

  const handleArchived = async (row: any, isCurrentlyArchived: boolean) => {
    try {
      const { unAuthenticatedCall: archiveCMS } = actionsHandler("cmsContent");

      const payload = {
        is_archive: !isCurrentlyArchived,
      };

      const response = await dispatch(
        archiveCMS({
          payload,
          method: "patch",
          customParams: [row.id + "/"],
        }),
      );

      if (response.ok) {
        toast.success(
          response.data?.message ||
            `CMS Page ${payload.is_archive ? "archived" : "unarchived"} successfully`,
        );
        setForceRefresh(true);
      } else {
        if (Array.isArray(response.data.message)) {
          response.data.message.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error(
            response?.data?.message ||
              `Failed to ${payload.is_archive ? "archive" : "unarchive"} CMS Page`,
          );
        }
      }
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Failed to update archive status");
    }
  };

  useEffect(() => {
    setForceRefresh(true);
  }, [isArchiveView]);

  return (
    <div className="tw-relative">
      <HeaderComponent />
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a
                href="/"
                className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
              >
                <img
                  src={BackIcon}
                  alt="Back Icon"
                  className="tw-cursor-pointer"
                />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                CMS Page
              </h1>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-mt-[147px]">
        <div className="tw-w-full">
          <div className="tw-border tw-border-t-0 tw-border-gray-100">
            <CustomTable
              columns={
                isArchiveView
                  ? [
                      {
                        header: "CMS Page Name",
                        accessor: "title",
                        sortable: true,
                        sortKey: "title",
                      },
                      {
                        header: "User Type",
                        accessor: "user_type",
                        sortable: true,
                        sortKey: "user_type",
                      },
                      {
                        header: "Created on",
                        accessor: (row: any) =>
                          toStandardDisplayDate({
                            inputDate: row.created_at,
                            inputFormat: "DD-MM-YYYY HH:mm:ss",
                            desiredFormat: "dateTime",
                          }),
                        sortable: true,
                        sortKey: "created_at",
                      },
                      {
                        header: "Archived on",
                        accessor: (row: any) =>
                          toStandardDisplayDate({
                            inputDate: row.updated_at,
                            inputFormat: "DD-MM-YYYY HH:mm:ss",
                            desiredFormat: "dateTime",
                          }),
                        sortable: true,
                        sortKey: "updated_at",
                      },
                      {
                        header: "Action",
                        accessor: (row: any) => (
                          <button
                            className="tw-text-blue-500"
                            onClick={() => handleArchived(row, isArchiveView)}
                          >
                            <img
                              src={UnarchiveIcon}
                              alt="Unarchive Icon"
                              className="tw-w-6 tw-h-6"
                            />
                          </button>
                        ),
                      },
                    ]
                  : [
                      {
                        header: "CMS Page Name",
                        accessor: "title",
                        sortable: true,
                        sortKey: "title",
                      },
                      {
                        header: "User Type",
                        accessor: "user_type",
                        sortable: true,
                        sortKey: "user_type",
                      },
                      {
                        header: "Created on",
                        accessor: (row: any) =>
                          toStandardDisplayDate({
                            inputDate: row.created_at,
                            inputFormat: "DD-MM-YYYY HH:mm:ss",
                            desiredFormat: "dateTime",
                          }),
                        sortable: true,
                        sortKey: "created_at",
                      },
                      {
                        header: "Action",
                        accessor: (row: any) => (
                          <div className="tw-flex tw-items-center tw-gap-5">
                            <button
                              onClick={() =>
                                navigate(`/edit-cms-page/${row.id}`)
                              }
                            >
                              <img
                                src={EditIcon}
                                alt="Edit Icon"
                                className="tw-w-6 tw-h-6"
                              />
                            </button>
                            <button
                              className="tw-text-blue-500"
                              onClick={() => handleArchived(row, isArchiveView)}
                            >
                              <img
                                src={DownloadIcon}
                                alt="Archive Icon"
                                className="tw-w-6 tw-h-6"
                              />
                            </button>
                          </div>
                        ),
                      },
                    ]
              }
              fetchData={fetchData}
              forceRefresh={forceRefresh}
              setForceRefresh={setForceRefresh}
              filterOptions={{
                show: true,
                options: categories.map((cat) => cat.user_type),
                placeholder: "Select User Type",
                onFilterChange: (selected: string[]) => {
                  setSelectedUserType(selected[0] || null);
                  setForceRefresh(true);
                },
              }}
              addButton={{
                text: "Add",
                icon: PlusIcon,
                onClick: () => navigate("/add-cms-page"),
              }}
              archiveButton={{
                text: isArchiveView ? "Back to List" : "Archived",
                icon: isArchiveView ? ListIcon : DownloadIcon,
                onClick: () => setIsArchiveView(!isArchiveView),
                isArchiveView,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSPage;
